import axios, { AxiosResponse, AxiosError } from 'axios';

export interface PaymentCard {
  number: string;
  recipient_name?: string;
  comment?: string;
  // VES-specific fields
  pago_movil?: string;
  bank_name?: string;
  cedula?: string;
}

export interface PaymentData {
  id: string;
  amount: number;
  currency: string;
  status: string;
  card?: PaymentCard;
  details: {
    redirect_url?: string;
    support_url?: string;
    // VES-specific fields (can come from details instead of card)
    phone_number?: string;
    pago_movil?: string;
    bank_name?: string;
    cedula?: string;
    recipient_name?: string;
    // Confirm with reference support
    requires_confirm_ref?: boolean;
  };
  expires_at: string;
  created_at: string;
}

export interface ConfirmPaymentResponse {
  id: string;
  amount: number;
  currency: string;
  status: string;
  card?: PaymentCard;
  details: {
    redirect_url?: string;
    support_url?: string;
  };
  expires_at: string;
  created_at: string;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.pagoseguropro.com/api/v2/internal';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 1000 * 20,
  headers: {
    'Content-Type': 'application/json',
  },
});

const normalizeUrl = (url: string): string => {
  if (url.endsWith('/')) {
    return url;
  }

  const queryIndex = url.indexOf('?');
  if (queryIndex !== -1) {
    return url.slice(0, queryIndex) + '/' + url.slice(queryIndex);
  }

  return url + '/';
};

apiClient.interceptors.request.use(
  (config) => {
    if (config.url && !config.url.includes('confirm')) {
      config.url = normalizeUrl(config.url);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError) => {
    if (error.response) {
      console.error('Response error:', error.response.status, error.response.data);
    } else if (error.request) {
      console.error('Request error:', error.request);
    } else {
      console.error('Error:', error.message);
    }
    return Promise.reject(error);
  }
);

export class PaymentApiService {
  static async getPayment(paymentId: string): Promise<PaymentData> {
    try {
      const response = await apiClient.get<PaymentData>(`/payment/${paymentId}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          throw new Error('Payment not found');
        } else if (error.response?.status === 401) {
          throw new Error('Unauthorized access');
        } else {
          // @ts-ignore
          if (error.response?.status >= 500) {
            throw new Error('Server error. Please try again later');
          } else if (error.code === 'ECONNABORTED') {
            throw new Error('Request timeout. Please check your connection');
          }
        }
      }

      throw new Error('Failed to fetch payment data');
    }
  }

  static async confirmPayment(paymentId: string, ref?: string): Promise<ConfirmPaymentResponse> {
    try {
      const body = ref ? { ref } : undefined;
      const response = await apiClient.post<ConfirmPaymentResponse>(`/payment/${paymentId}/confirm`, body);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          throw new Error('Payment not found');
        } else if (error.response?.status === 401) {
          throw new Error('Unauthorized access');
        } else {
          // @ts-ignore
          if (error.response?.status >= 500) {
            throw new Error('Server error. Please try again later');
          } else if (error.code === 'ECONNABORTED') {
            throw new Error('Request timeout. Please check your connection');
          }
        }
      }

      throw new Error('Failed to confirm payment');
    }
  }
}
